import type { TablePillTone } from './TablePill.props';

/**
 * Choosing a pill tone.
 *
 * Two different problems, one palette:
 *
 * - **Priorities** carry a colour configured per client/contract. That intent is
 *   worth keeping — someone chose red for HSO and blue for scheduled work — but
 *   the raw hex is not: it arrives as full-saturation Material 500 (#ff1744,
 *   #2196f3) and was rendered as that hex over a 9%-alpha wash of itself, which
 *   measures under 3:1. So the configured colour is read for its *hue* and
 *   snapped onto the pill's measured tones.
 * - **Statuses** have no configured colour at all, so they are mapped by
 *   meaning.
 */

/** Hue in degrees (0–360) and saturation (0–1) of a `#rgb` / `#rrggbb`. */
const hueAndSaturation = (hex: string): { hue: number; saturation: number } | null => {
  const value = hex.trim().replace('#', '');
  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) return null;

  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;
  if (delta === 0) return { hue: 0, saturation: 0 };

  const saturation = delta / (1 - Math.abs(2 * lightness - 1));

  let hue: number;
  if (max === r) hue = ((g - b) / delta + 6) % 6;
  else if (max === g) hue = (b - r) / delta + 2;
  else hue = (r - g) / delta + 4;

  return { hue: hue * 60, saturation };
};

/**
 * The tone a configured priority colour maps to.
 *
 * Anything too washed out to read as a hue keeps the neutral tone rather than
 * being forced into a category it was never meant to signal.
 */
export const toneFromConfiguredColor = (color: string | undefined): TablePillTone => {
  if (!color) return 'neutral';
  const hsl = hueAndSaturation(color);
  if (!hsl || hsl.saturation < 0.15) return 'neutral';

  const { hue } = hsl;
  if (hue < 20 || hue >= 320) return 'negative'; // red, crimson, magenta
  if (hue < 65) return 'warning'; // orange, amber, yellow
  if (hue < 175) return 'positive'; // green, teal
  return 'info'; // cyan through violet
};

/**
 * The tone a status maps to — service statuses and shipment statuses alike,
 * since the same word should not mean two different things in two places.
 *
 * Grouped by what the status tells whoever is scanning the list — is this job
 * moving, is it stuck, or has it gone wrong — rather than by where it sits in
 * the lifecycle. That is the question a dispatcher is actually asking, and it
 * keeps a 26-status column readable at a glance instead of turning it into a
 * colour legend nobody memorises.
 */
// A Map, not an object literal: the keys come from configuration, and a plain
// object would answer `STATUS_TONES['constructor']` with a function rather than
// falling through to the neutral default.
const STATUS_TONES = new Map<string, TablePillTone>(
  Object.entries({
    // Moving, or finished well: someone is actively working the job.
    'on the way': 'positive',
    'on site': 'positive',
    examining: 'positive',
    repairing: 'positive',
    testing: 'positive',
    'parts available': 'positive', // the blocker cleared
    complete: 'positive',

    // Waiting on something outside the technician's control.
    'parts required': 'warning',
    'parts in order': 'warning',
    'parts in transit': 'warning',
    'parts in repair': 'warning',
    'waiting return': 'warning',
    'third party repair': 'warning',
    'callback required': 'warning',
    'on hold': 'warning',

    // Gone wrong, or gone away — these are the ones worth spotting first.
    cancelled: 'negative',
    exception: 'negative',
    escalated: 'negative',
    futile: 'negative',

    // Accepted and planned, but not yet under way.
    new: 'info',
    available: 'info',
    acknowledged: 'info',
    'ready for booking': 'info',
    scheduled: 'info',
    dispatched: 'info',
    'with logistics': 'info',

    // Shipments, by name and by code — the shipment list keys off the code, the
    // detail panels render the name. `cancelled` and `complete` are shared with
    // the service statuses above and deliberately mean the same thing here.
    pending: 'warning',
    partial: 'negative', // short-shipped, which someone has to act on
    'in transit': 'info',
    'in-transit': 'info',
    received: 'positive',
  })
);

/** Falls back to neutral, so a status added later renders legibly rather than
 *  inheriting a colour that would claim a meaning nobody chose. */
export const toneForStatus = (status: string | undefined): TablePillTone =>
  STATUS_TONES.get(status?.trim().toLowerCase() ?? '') ?? 'neutral';

/**
 * Severity words, for the priorities that arrive as a name rather than a
 * client-configured code. The detail panels show "Critical" / "High" with no
 * colour attached, so without this they would all render neutral and lose a
 * distinction the list keeps.
 */
const PRIORITY_NAME_TONES = new Map<string, TablePillTone>(
  Object.entries({
    critical: 'negative',
    urgent: 'negative',
    high: 'warning',
    medium: 'info',
    normal: 'info',
    standard: 'info',
    low: 'neutral',
  })
);

/**
 * The tone for a priority: the colour configured against it wins, and its name
 * is the fallback. Both paths land on the same five tones, so a priority reads
 * the same on the Jobs list as it does on the job itself.
 */
export const toneForPriority = (
  priority: string | undefined,
  configuredColor?: string
): TablePillTone => {
  const fromColor = toneFromConfiguredColor(configuredColor);
  if (fromColor !== 'neutral') return fromColor;
  return PRIORITY_NAME_TONES.get(priority?.trim().toLowerCase() ?? '') ?? 'neutral';
};
